import * as React from 'react';
import {DataGrid, gridClasses, useGridApiRef} from '@mui/x-data-grid';
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {Themes} from "./Themes";
import ExportButton from "./ExportButtons";
import DOMPurify from "dompurify";
import PropTypes from "prop-types";
import TableDataGrid from "./TableDataGrid";
import {useRef} from "react";

export default function FrontEndTable({title, subtitle, data, site, exportData, variant = ''}) {
  const theme = createTheme(Themes(site, variant));
  const apiRef = useGridApiRef(); // Access the DataGrid API
  const columnObject = data.shift();
  const columnAlignments = columnObject.alignment ?? {};
  const variantClass = variant !== '' ? '--' + variant : '';

  const renderHandler = (params) => {
    if (drupalSettings.feature_toggle.enabled.includes('table_field_markup_3612') === false) {
      return params.value;
    }
    return <div
      dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(params.value, {ALLOWED_TAGS: ['strong', 'a', 'i']})}}/>;
  }

  const columns = Object.entries(columnObject)
    .filter(([key, _]) => key.startsWith('col')) // Assuming you only want properties that start with 'col'
    .map(([field, headerName]) => ({
      field,
      headerName,
      flex: 1,
      minWidth: 200,
      editable: false,
      align: columnAlignments[field] ?? 'left',
      renderCell: renderHandler,
    })).sort((a, b) => {
      if (a.field < b.field) {
        return -1;
      }
      if (a.field > b.field) {
        return 1;
      }
      return 0;
    });

  const exportRef = useRef();

  return (
    <ThemeProvider theme={theme}>
      <div className={'a-table-header ' + variantClass}>
        <div className={'title-section'}>
          {title && <h2 className="a-table-header__title">{title} </h2>}
          {subtitle && <p className="a-table-header__text">{subtitle}</p>}
        </div>

        <ExportButton tableRef={exportRef} tableData={exportData} dataGrid={apiRef} columns={columns} rows={data} filename={title}/>
      </div>
      <DataGrid
        apiRef={apiRef}
        sx={{
          border: 0,
          borderRadius: 0,
          borderColor: theme.palette.border.main,
          backgroundColor: theme.palette.background.default,
          ['&']: {
            borderLeft: '1px solid ' + theme.palette.border.main,
            borderRight: '1px solid ' + theme.palette.border.main,
          },
          [`& .${gridClasses.row}:hover, & .${gridClasses.row}.Mui-selected, & .${gridClasses.row}.Mui-selected:hover`]: {
            backgroundColor: "inherit"
          },
          [`& .${gridClasses.row}.even, & .${gridClasses.row}.even.Mui-selected:hover`]: {
            backgroundColor: theme.palette.odd.main
          },
          ['.MuiDataGrid-columnHeaderTitle']: {
            fontWeight: "bold"
          },
          '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
            fontSize: "14px",
            padding: "6px 16px",
            minHeight: "32px !important", // to force overwrite
          },
          '& .MuiDataGrid-iconSeparator': {
            color: theme.palette.border.main,
          },
          '& .MuiTablePagination-displayedRows': {
            marginBottom: 0
          },
          '.MuiDataGrid-withBorderColor': {
            border: 0,
          },
          '& .MuiIconButton-root': {
            color: theme.palette.primary.contrastText
          },
          '& .MuiIconButton-root.Mui-disabled': {
            color: theme.palette.text.disabled
          }
        }}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 32}
        rows={data}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10]}
      />
      <TableDataGrid tableRef={exportRef} data={exportData}/>
    </ThemeProvider>
  );
}

FrontEndTable.propTypes = {
  data: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  site: PropTypes.string.isRequired,
  exportData: PropTypes.array.isRequired,
  variant: PropTypes.string
};
